import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/sharp-solid-svg-icons';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { SCView } from '../view';
import type { SCCodeEditorProps } from './editor';
import { SCCodeEditor } from './editor';
import type { SCCodeFragmentProps } from './fragment';
import { SCCodeFragment } from './fragment';
import './styles.css';

interface SCCodeEditorAndFragmentProps extends SCCodeEditorProps, SCCodeFragmentProps {
  fragmentTitle?: string;
  editorTitle?: ReactNode;
  maxHeight?: string | number; // Maximum height for the editor
  actions?: { label: string; onPress: () => void; disabled?: boolean; loading?: boolean }[];
}

function SCCodeEditorAndFragment({
  language,
  value,
  onChange,
  disabled,
  autoCompleteOptions,
  items,
  fragmentTitle,
  editorTitle,
  actions,
  maxHeight,
}: SCCodeEditorAndFragmentProps) {
  const customAttributes = useMemo(() => {
    return items.map((item) => item.children.map((child) => child.value)).flat();
  }, [items]);
  return (
    <SCView>
      {actions?.length && (
        <SCView justifyEnd flex gap={6} mb={2}>
          {actions.map((action) => (
            <div
              style={{
                cursor: action.disabled || action.loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
              key={action.label}
              onClick={() => {
                if (!(action.disabled || action.loading)) {
                  action.onPress();
                }
              }}
            >
              <span
                className={`sce-action-label ${action.disabled || action.loading ? 'disabled' : ''}`}
              >
                {action.label}
              </span>
              {action.loading && <FontAwesomeIcon spin icon={faSpinnerThird} />}
            </div>
          ))}
        </SCView>
      )}
      <div className="sc-editor-fragment-container">
        {!!items.length && (
          <SCView
            className="sc-code-editor-fragment-panel"
            flex
            flexCol
            gap={2}
            w={'40%'}
            maxW={'300px'}
          >
            {fragmentTitle && (
              <SCView textSm h={5}>
                {fragmentTitle}
              </SCView>
            )}
            {/* Explicit height = editor height + label (~22px) + gap (8px) = maxHeight+30.
                This gives SCCodeFragment a definite parent so height:100% resolves and
                overflow-y:auto scrolls instead of the panel growing with GV content. */}
            <SCView
              flex
              overflowYAuto
              style={{
                height:
                  typeof maxHeight === 'number'
                    ? maxHeight + 30
                    : `calc(${maxHeight ?? '400px'} + 30px)`,
              }}
            >
              <SCCodeFragment disabled={disabled} items={items} language={language} />
            </SCView>
          </SCView>
        )}
        <SCView
          flex1
          flex
          flexCol
          gap={2}
          style={{ minWidth: 0, padding: 10, backgroundColor: 'var(--surface-base-light)' }}
        >
          {editorTitle && <SCView>{editorTitle}</SCView>}
          <SCView
            className="sc-code-editor-scroll-wrapper"
            flex1
            flex
            overflowYAuto
            style={{
              minHeight: maxHeight ?? '400px',
              maxHeight: maxHeight ?? '400px',
            }}
          >
            <SCCodeEditor
              disabled={disabled}
              language={language}
              autoCompleteOptions={autoCompleteOptions}
              value={value}
              onChange={onChange}
              customAttributes={customAttributes}
              maxHeight={maxHeight}
            />
          </SCView>
        </SCView>
      </div>
    </SCView>
  );
}

export { SCCodeEditorAndFragment };
export type { SCCodeEditorAndFragmentProps };
